import React, { useEffect, useRef } from "react";
import { useObserver } from "mobx-react";
import { compact } from "lodash";
import { Grid, makeStyles, Typography, TextField } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import { durationsToWeeks, moment, weeksToDurations } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { LegalPersonType } from "@bonliva-crm/shared";
import { ThemeType } from "@theme";
import {
  CustomerType,
  ProjectVisibilityContext,
  useGetBookingEditTravelZonesQuery
} from "@graphql";
import { SearchSelect } from "@common/Select/SearchSelect";
import { Body, DepartmentSelect, RegularButton, WithTooltip } from "@components";
import { Date, Form, RadioGroup, Text } from "@components/formx";
import { Select } from "@components/formx_item_form";
import { getOptions } from "@components/CostCenterSelect";
import ConsultantSelector from "@containers/VirtualSelector/ConsultantSelector/ConsultantSelector";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";
import { WorkRequestPeriods } from "../components/week-periods";
import { BookingEditStore } from "./BookingEditStore";
import { BookingCustomerNumber } from "../shared/BookingCustomerNumber";
import { BookingContactPerson } from "../shared/BookingContactPersons/BookingContactPersons";
import { BookingLegalPersonTypeSection } from "../shared/BookingLegalPersonTypeSection";
import { ProjectSelector } from "../ProjectSelector/ProjectSelector";
import { MissingCustomerNumberError } from "../components/MissingCustomerNumberError";
import { StaffingPortalBlockedError } from "../components/staffing-portal-blocked-error";
import { DirectRequestContractModal } from "./DirectRequestContractModal";

type Props = {
  store: BookingEditStore;
};

export default function BookingEditForm(props: Props) {
  const { store } = props;

  const aoeRef = useRef(null);
  const classes = useStyles({});

  const { data: travelZonesData } = useGetBookingEditTravelZonesQuery();

  useEffect(() => {
    if (!aoeRef.current) {
      return;
    }

    setTimeout(() => {
      aoeRef.current.selectRef.current.state.isOpen = !store.isUpdating;
      aoeRef.current.selectRef?.current.focus();
    }, 600); // transitions.js -> complex (375) + enteringScreen (225)
  }, [aoeRef.current, store.isUpdating]);

  return useObserver(() => (
    <Form store={store} allowUpdates>
      <Grid
        container
        alignItems={"center"}
        spacing={1}
        data-test="BookingForm"
        className={classes.root}
      >
        <Grid item xs={12}>
          <Select
            label={locale.AreaOfExpertise}
            name="AreaOfExpertise"
            options={store.allAreasOfExpertise.toLabelValue()}
            innerRef={aoeRef}
            required
            disabled={!store.isNew || store.isDuplicating}
          />

          <Select
            label={locale.Specializations}
            name="Specializations"
            multi
            options={store.allSpecializations.toLabelValue()}
            disabled={!store.values.AreaOfExpertise}
          />
        </Grid>

        {store.isNationalProcurementAvailable && (
          <Grid item xs={12}>
            <RadioGroup
              row
              label={locale.NationalProcurementOption}
              name="isNationalProcurement"
              options={[
                {
                  label: locale.Yes,
                  value: "true"
                },
                {
                  label: locale.No,
                  value: "false"
                }
              ]}
              disabled={!store.isNew}
            />
          </Grid>
        )}

        {store.isMainFormAvailable && (
          <>
            <Grid item xs={12}>
              <CustomerSelector
                disabled={store.isNew ? false : store.disabledWeeks.length > 0}
                fullWidth
                required
                error={store.getError("HospitalRef")}
                disabledFilterFields={["customerType"]}
                initialFilter={{
                  customerType: CustomerType.Hospital
                }}
                onChange={store.setHospital}
                value={
                  store.values.HospitalRef
                    ? {
                        label: store.values.HospitalRef.label || "missing",
                        value: store.values.HospitalRef.value
                      }
                    : null
                }
              />
            </Grid>

            {store.blockedStaffingPortal && (
              <Grid item xs={12} style={{ marginTop: -8 }}>
                <StaffingPortalBlockedError portal={store.blockedStaffingPortal} />
              </Grid>
            )}

            <Grid item xs={12}>
              <DepartmentSelect
                disabled={
                  store.values.HospitalRef ? store.disabledWeeks.length > 0 : false
                }
                error={store.getError("DepartmentsRefs")}
                options={store.allDepartments}
                onChange={(v) => store.setDepartments(v)}
                value={store.values.DepartmentsRefs}
              />
            </Grid>

            {store.values.AreaOfExpertise && store.values.HospitalRef && (
              <Grid item xs={12} style={{ marginTop: -8 }}>
                <BookingCustomerNumber
                  areaOfExpertise={store.values.AreaOfExpertise}
                  hospitalId={store.values.HospitalRef.value}
                  departmentIds={store.values.DepartmentsRefs || []}
                  onFetch={store.setCustomerNumber}
                />

                {store.showMissingCustomerNumberError && <MissingCustomerNumberError />}
              </Grid>
            )}

            {store.values.HospitalRef && (
              <Grid item xs={12} style={{ marginTop: -8 }}>
                <BookingContactPerson
                  hospitalId={store.values.HospitalRef.value}
                  areaOfExpertise={store.values.AreaOfExpertise}
                  departmentIds={store.values.DepartmentsRefs || []}
                  onFetch={() => null}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <ConsultantSelector
                clearable
                error={store.getError("Consultant")}
                fullWidth
                required
                initialFilter={{
                  areasOfExpertise: compact([store.values.AreaOfExpertise]),
                  text: ""
                }}
                onChange={store.setConsultant}
                value={store.getValue("Consultant") as any}
                disabled={!store.isNew}
              />

              {!store.isNew && (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  style={{ paddingTop: 4 }}
                >
                  För att kunna ändra konsulten behöver denna bokningen avbokas och läggas
                  till på nytt
                </Typography>
              )}
            </Grid>

            {store.consultantData && (
              <Grid item xs={12}>
                <BookingLegalPersonTypeSection
                  legalPersonTypeName="LegalPersonType"
                  businessName="BusinessName"
                  businessSSNName="BusinessSocialSecurityNo"
                  updateConsultantName="UpdateConsultantWithSnapshotData"
                  changeMessage={locale.ConsultantCurrentFinanceInformation(
                    locale.legalPersonType[store.consultantData.LegalPersonType],
                    store.consultantData.BusinessName,
                    store.consultantData.BusinessSocialSecurityNo
                  )}
                  legalPersonTypeDisabled={!!store.legalPersonType}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              {store.errors.Weeks && (
                <Body color="error">{store.errors.Weeks.toString()}</Body>
              )}

              <WorkRequestPeriods
                {...store.dateSelectorDates}
                disabled={
                  !store.values.Consultant?.value ||
                  !store.isNew
                }
                bookings={[]}
                disabledWeeks={store.disabledWeeks}
                durations={[]}
                onChange={(v) => {
                  store.setPeriod(durationsToWeeks(v));
                }}
                value={weeksToDurations(store.values.Weeks)}
              />

              <div style={{ display: "flex", marginTop: 4 }}>
                <RegularButton
                  variant="outlined"
                  onClick={store.setDateBack}
                  size="small"
                  style={{ marginRight: "auto" }}
                >
                  {locale.Prev}
                </RegularButton>

                <RegularButton variant="outlined" size="small" onClick={store.setDateFwd}>
                  {locale.Next}
                </RegularButton>
              </div>
            </Grid>

            <Grid item xs={12} style={{ position: "relative" }}>
              <SearchSelect
                sortOptions={false}
                label={locale.ConsultantContracts}
                options={store.consultantContractsOptions}
                value={store.values.consultantContractId}
                onChange={store.handleConsultantContract}
                disabled={!store.values.Weeks.length || !store.values.Consultant?.value}
                required={store.isConsultantContractRequired}
                error={store.isConsultantContractRequired}
              />
            </Grid>

            <Grid item xs={12}>
              <Select
                disabled={
                  !!store.values.consultantContractId ||
                  store.values["LegalPersonType"] == LegalPersonType.Subcontractor ||
                  store.values.isNationalProcurement === "true"
                }
                name="ContractType"
                label={locale.ContractType}
                options={store.contractTypes.toLabelValue((x) => locale.contractType[x])}
                required={!!store.isContractTypeEnabled}
              />
            </Grid>

            <WithTooltip
              title={
                !store.disabledCustomerContract
                  ? locale.CustomerContracts
                  : "Du kan inte slutföra denna åtgärd då en bokningsvecka i denna bokningsgruppen redan är hanterad."
              }
            >
              <Grid item xs={12} style={{ position: "relative" }}>
                <SearchSelect
                  label={locale.CustomerContracts}
                  options={store.customerContractsOptions}
                  value={store.values.customerContractId}
                  onChange={store.handleCustomerContract}
                  disabled={store.disabledCustomerContract}
                  required={store.isCustomerContractRequired}
                  error={store.isCustomerContractRequired}
                />

                {store.errors["datesOutOfContract"] && (
                  <Typography variant="body1" color="error" style={{ paddingTop: 4 }}>
                    {store.errors["datesOutOfContract"]}
                  </Typography>
                )}
              </Grid>
            </WithTooltip>

            {store.values.isNationalProcurement === "false" ? (
              <Grid item xs={12}>
                <WithTooltip
                  placement="top"
                  title={store.directRequestButtonTooltip}
                >
                  <span>
                    <RegularButton
                      variant="outlined"
                      size="small"
                      disabled={!store.canCreateDirectRequestContract}
                      onClick={store.openDirectRequestModal}
                    >
                      {locale.directRequestContract.button}
                    </RegularButton>
                  </span>
                </WithTooltip>
                {store.lastCreatedDirectRequestContractUri && (
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    <a
                      href={store.lastCreatedDirectRequestContractUri}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {locale.directRequestContract.openContract}
                    </a>
                  </Typography>
                )}
              </Grid>
            ) : null}

            {(!store.isNew &&
              store.values.subVersionId !== null &&
              store.values.isNationalProcurement === "true") ||
            store.values.isNationalProcurement !== "true" ? (
              <Grid item xs={7}>
                <Select
                  disabled={
                    store.disabledCustomerContract || !store.values.customerContractId
                  }
                  name="subVersionId"
                  label={locale.Subversion}
                  options={store.subVersionsContractsOptions}
                />
              </Grid>
            ) : null}

            {store.values.isNationalProcurement === "true" ? (
              <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body1">
                  Priset beror på vilket område du har lagt in på bokningen. Det är
                  viktigt att du lägger in rätt område då detta också kopplar till
                  artikelnummer. För “Annan specialitet” så hanterar du det längre ner i
                  formuläret.
                </Typography>
              </Grid>
            ) : null}

            {store.values.isNationalProcurement === "false" ? (
              <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                <RegularButton
                  variant="outlined"
                  disabled={
                    !store.values.customerContractId || !store.values.Consultant?.value
                  }
                  onClick={store.addSubVersion}
                  size="small"
                  style={{ marginRight: 10 }}
                >
                  {locale.AddSubVersion}
                </RegularButton>
                <WithTooltip placement={"top"} title={locale.AddSubVersionInfo}>
                  <Info />
                </WithTooltip>
              </Grid>
            ) : null}

            {store.isNationalProcurementTravelQuestion && (
              <>
                <Grid item xs={12}>
                  <RadioGroup
                    key={"nationalProcurementTravelRequired"}
                    row
                    label={locale.NationalProcurementTravelQuestion}
                    name="nationalProcurementTravelRequired"
                    options={[
                      {
                        label: locale.Yes,
                        value: "true"
                      },
                      {
                        label: locale.No,
                        value: "false"
                      }
                    ]}
                  />
                </Grid>

                {store.values.nationalProcurementTravelRequired === "true" && (
                  <Grid item xs={12}>
                    <Select
                      name="travelTemplateLevel"
                      label={locale.TravelTemplateLevel}
                      options={
                        travelZonesData?.config.defaultTravelZones.map((x) => ({
                          label: x.label,
                          value: x.name
                        })) || []
                      }
                      required
                    />
                  </Grid>
                )}
              </>
            )}

            {store.shouldShowTravelInvoicing ? (
              <div>
                <Typography
                  variant="body1"
                  color={store.errors.travelInvoicingWeeks ? "error" : "inherit"}
                >
                  Ange här alla veckor som du på förhand vet att vi kommer kunna fakturera
                  resa för:
                </Typography>
                <WorkRequestPeriods
                  {...store.dateTravelInvoicingSelectorDates}
                  bookings={weeksToDurations(store.values.Weeks)}
                  disableNotBooked
                  skipDisabledSpans
                  onChange={(v) => {
                    store.setTravelInvoicingWeeks(durationsToWeeks(v));
                    store.validate();
                  }}
                  value={weeksToDurations(store.values.travelInvoicingWeeks)}
                />

                <div style={{ display: "flex", marginTop: 4 }}>
                  <RegularButton
                    variant="outlined"
                    onClick={store.setTravelInvoicingDateBack}
                    size="small"
                    style={{ marginRight: "auto" }}
                  >
                    {locale.Prev}
                  </RegularButton>

                  <RegularButton
                    variant="outlined"
                    size="small"
                    onClick={store.setTravelInvoicingDateFwd}
                  >
                    {locale.Next}
                  </RegularButton>
                </div>
              </div>
            ) : null}

            <Grid item xs={12}>
              <ProjectSelector
                visibilityContext={ProjectVisibilityContext.CREATE_BOOKING_FORM}
                ProjectSelectProps={{ disabled: !store.isNew }}
                clearable
                store={store.projectSelectorStore}
                error={Boolean(store.errors.ProjectId)}
                value={store.values.ProjectId}
                onChange={(v) => store.setValue("ProjectId", v)}
              />
            </Grid>

            <Grid item xs={12}>
              <Select
                disabled={!store.isNew}
                name="CostCenter"
                label={locale.CostCenter}
                options={getOptions(store.costCenters)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <RadioGroup
                row
                label={`${locale.BookingIntro}?`}
                name="bookingIntro.required"
                options={[
                  {
                    label: locale.Yes,
                    value: "true"
                  },
                  {
                    label: locale.No,
                    value: "false"
                  }
                ]}
              />
              {store.values.bookingIntro?.required === "true" && (
                <>
                  <Date
                    utc
                    DatePickerProps={{
                      // minDate:,
                      startDate: moment().toDate(),
                      shouldCloseOnSelect: true
                    }}
                    name="bookingIntro.startDate"
                    // transformValue={v => moment(v).format("YYYY-MM-DD")}
                    label={locale.StartDate}
                  />
                  <Date
                    utc
                    DatePickerProps={{
                      minDate: moment(store.values.bookingIntro.startDate).toDate(),
                      startDate: moment().toDate(),
                      shouldCloseOnSelect: true
                    }}
                    name="bookingIntro.endDate"
                    label={locale.EndDate}
                  />
                  <Text name="bookingIntro.description" label={locale.Description} />
                </>
              )}
            </Grid>

            {store.showDiscountQuestion && (
              <Grid xs={12}>
                <RadioGroup
                  row
                  label={locale.TenPercentDiscountQuestion}
                  name="isDiscount"
                  options={[
                    {
                      label: locale.Yes,
                      value: "true"
                    },
                    {
                      label: locale.No,
                      value: "false"
                    }
                  ]}
                />
              </Grid>
            )}

            {store.values.isNationalProcurement === "true" && (
              <Grid xs={12}>
                <Text
                  name="authorizedOrderer"
                  label={locale.AuthorizedOrderer}
                  disabled={store.disabledAuthorizedOrderer}
                  TextFieldProps={{ required: true }}
                />
              </Grid>
            )}
            {store.values.isNationalProcurement != null && (
              <Grid xs={12}>
                <TextField
                  name="callNumber"
                  label={locale.CallNumberLabel}
                  onChange={(e) => {
                    store.setValue("callNumber", e.target?.value?.replace(/\s+/g, ""));
                  }}
                  required={store.isRequiredCallNumber}
                  error={store.isRequiredCallNumber}
                  value={store.values.callNumber}
                  fullWidth
                  InputLabelProps={{
                    shrink: store.values.callNumber ? true : undefined
                  }}
                  InputProps={{
                    endAdornment: (
                      <WithTooltip placement={"top"} title={store.callNumberTooltip}>
                        <Info />
                      </WithTooltip>
                    )
                  }}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
      <DirectRequestContractModal store={store} />
    </Form>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    "& > div": {
      margin: theme.spacing()
    }
  },

  usersSelect: {
    alignItems: "center",
    display: "flex"
  },

  projectSelectBtns: {
    ...theme.mixins.childrenMarginRight(4),
    marginBottom: 4,
    display: "flex",
    alignItems: "flex-end"
  }
}));

export const customerContractOptionDescription = (options: string[]) => {
  return options.map((option, i) => (
    <div key={i} style={{ paddingLeft: 15 }}>
      -- {option}
    </div>
  ));
};
